// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Settings, RefreshCw,
  Trash2, ChevronRight, AlertTriangle,
} from 'lucide-react'
import { BackButton }  from '@/components/ui/BackButton'
import { Card }        from '@/components/ui/Card'
import { Button }      from '@/components/ui/Button'
import { DemoResetButton } from '@/components/demo/DemoResetButton'
import { hardResetWizard } from '@/store/wizardStore'
import { WIZARD_ROUTE, ROUTES } from '@/lib/constants/routes'
import { Link } from 'react-router-dom'

export function SettingsPage() {
  const navigate              = useNavigate()
  const [language, setLanguage] = useState('nl')
  const [confirmWipe, setConfirmWipe] = useState(false)

  async function handleWipeData() {
    if (!confirmWipe) { setConfirmWipe(true); return }

    // Stap 1: wis volledige IndexedDB (alle 19 tabellen incl. user_profiles, module_progress)
    await new Promise<void>((resolve) => {
      const req = indexedDB.deleteDatabase('WilsAppDB')
      req.onsuccess = () => resolve()
      req.onerror   = () => resolve()
      req.onblocked = () => resolve()
    })

    // Stap 2: wis alle wilsapp_* localStorage keys (incl. wizard state, access_granted)
    Object.keys(localStorage)
      .filter(k => k.startsWith('wilsapp'))
      .forEach(k => localStorage.removeItem(k))

    // Stap 3: wis alle wilsapp_* sessionStorage keys
    Object.keys(sessionStorage)
      .filter(k => k.startsWith('wilsapp'))
      .forEach(k => sessionStorage.removeItem(k))

    // Stap 4: reset wizard in-memory state
    hardResetWizard()

    // Stap 5: herlaad pagina - app start als nieuw
    window.location.href = '/demo/'
  }



  const SECTIONS = [
    {
      title: 'Taal',
      items: [
        {
          label: 'Taal van de app',
          desc:  'Momenteel: Nederlands',
          control: (
            <select value={language} onChange={e => setLanguage(e.target.value)}
              className="rounded-lg border border-sand-300 bg-white px-3 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-sage-400">
              <option value="nl">Nederlands</option>
            </select>
          ),
        },
      ],
    },
  ]

  return (
    <div className="flex flex-col gap-6 animate-fade-in">
      <BackButton />

      <div className="flex items-center gap-3">
        <Settings className="h-6 w-6 text-sage-600" />
        <h1 className="text-xl font-semibold text-ink">Instellingen</h1>
      </div>

      {/* Instellingen secties */}
      {SECTIONS.map(sec => (
        <div key={sec.title}>
          <p className="text-xs font-semibold text-ink-4 uppercase tracking-wider mb-2">{sec.title}</p>
          <Card>
            {sec.items.map((item, i) => (
              <div key={i} className={`flex items-center justify-between py-3 ${i > 0 ? 'border-t border-sand-200' : ''}`}>
                <div>
                  <p className="text-sm font-medium text-ink">{item.label}</p>
                  {item.desc && <p className="text-xs text-ink-4 mt-0.5">{item.desc}</p>}
                </div>
                {item.control}
              </div>
            ))}
          </Card>
        </div>
      ))}

      {/* Acties */}
      <div>
        <p className="text-xs font-semibold text-ink-4 uppercase tracking-wider mb-2">Acties</p>
        <Card>
          <div className="flex flex-col divide-y divide-sand-200">
            {/* Demo reset */}
            <button
              onClick={() => { hardResetWizard(); navigate(WIZARD_ROUTE) }}
              className="flex items-center justify-between py-3.5 text-left hover:bg-sand-50 rounded-xl transition-colors px-1 group"
            >
              <div className="flex items-center gap-3">
                <RefreshCw className="h-4 w-4 text-sage-600" />
                <div>
                  <p className="text-sm font-medium text-ink">Wizard opnieuw starten</p>
                  <p className="text-xs text-ink-4">Module-data blijft bewaard</p>
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-ink-4 group-hover:text-ink-3" />
            </button>

          </div>
        </Card>
      </div>

      {/* Gegevens wissen - danger zone */}
      <div>
        <p className="text-xs font-semibold text-ink-4 uppercase tracking-wider mb-2">Gevaarzone</p>
        <Card className="border-status-alertBorder">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="h-4 w-4 text-status-alertText flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-status-alertText">Alle gegevens wissen</p>
              <p className="text-xs text-ink-4 mt-0.5">
                Dit verwijdert al uw opgeslagen gegevens permanent. Dit kan niet ongedaan worden gemaakt.
              </p>
            </div>
          </div>

          {confirmWipe ? (
            <div className="flex flex-col gap-2">
              <p className="text-xs font-semibold text-status-alertText">
                Weet u het zeker? Klik nogmaals om alles te verwijderen.
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-status-alertBorder text-status-alertText hover:bg-status-alertBg"
                  onClick={handleWipeData}
                  icon={<Trash2 className="h-4 w-4" />}
                >
                  Ja, alles wissen
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirmWipe(false)}>
                  Annuleren
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="border-status-alertBorder text-status-alertText hover:bg-status-alertBg"
              onClick={handleWipeData}
              icon={<Trash2 className="h-4 w-4" />}
            >
              Gegevens wissen
            </Button>
          )}
        </Card>
      </div>

      {/* Juridisch */}
      <div>
        <p className="text-xs font-semibold text-ink-4 uppercase tracking-wider mb-2">Juridisch</p>
        <Card>
          <div className="flex flex-col divide-y divide-sand-200">
            <Link to={ROUTES.PRIVACY}
              className="flex items-center justify-between py-3.5 px-1 text-sm text-ink-3 hover:text-ink-2 hover:bg-sand-50 rounded-xl transition-colors">
              Privacyverklaring
              <ChevronRight className="h-4 w-4 text-ink-4" />
            </Link>
            <Link to={ROUTES.TERMS}
              className="flex items-center justify-between py-3.5 px-1 text-sm text-ink-3 hover:text-ink-2 hover:bg-sand-50 rounded-xl transition-colors">
              Algemene voorwaarden
              <ChevronRight className="h-4 w-4 text-ink-4" />
            </Link>
          </div>
        </Card>
      </div>

      <p className="text-center text-xs text-ink-4">WilsApp Pilot</p>

      {/* Demo-reset - alleen in DEMO_MODE zichtbaar */}
      <DemoResetButton />

    </div>
  )
}
